import type { PaymentRequestAttachment } from "Components/Chat/chatAttachmentTypes";
import { Box, Stack } from "@mui/material";
import { useMessageContext } from "stream-chat-react";
import { TransactionStatus } from "Infrastructure/Api/Api";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { useIsFullMoonePersona } from "Hooks/useIsFullMoonePersona";
import { canLiteViewerPay } from "Components/Chat/chatPaymentRules";
import { useTransactionPolling } from "Hooks/Chat/useTransactionPolling";
import { formatCurrency } from "Utils/CurrencyUtils";
import {
  statusVariants,
  defaultVariant,
} from "Components/Chat/transactionStatusUtils";
import { PaymentRequestCardDetails } from "Components/Chat/PaymentRequestCardDetails";
import { PaymentRequestStatusBar } from "Components/Chat/PaymentRequestStatusBar";

type Props = {
  attachment: PaymentRequestAttachment;
};

export const PaymentRequestCard = ({ attachment }: Props) => {
  const user = useLoggedInUser();
  const isFullMoone = useIsFullMoonePersona();
  const { message } = useMessageContext();

  const publicID = attachment.transactionPublicID;
  const { data } = useTransactionPolling(publicID);

  const amount = data?.amount ?? attachment.amount;
  const currencyCode = data?.currencyCode ?? attachment.currencyCode;
  const status = data?.transactionStatus ?? attachment.transactionStatus ?? "";
  // Counterparty name comes from the server transaction, never the client-controlled attachment —
  // a forged attachment must not be able to display a misleading recipient. There is no
  // server-side variable symbol for these payment requests, so we don't render one.
  const displayName = data?.creditorName;
  const isCurrentUserCreditor = user?.userID === data?.creditorID;
  const isAwaitingPayment =
    status === TransactionStatus.Initiated ||
    status === TransactionStatus.Created;
  // The author of the request is its creditor; a Lite viewer may pay a merchant's, never a
  // customer's (App ADR-0013) — the API would refuse it at pay time, the card says so upfront.
  const isPayRestricted =
    !isFullMoone &&
    !isCurrentUserCreditor &&
    !canLiteViewerPay(message.user?.accessRole);
  const canBePaid =
    isAwaitingPayment && !isCurrentUserCreditor && !isPayRestricted;
  const variant = statusVariants[status] ?? defaultVariant;
  const formattedAmount = formatCurrency({ amount, currencyCode });

  return (
    <Box
      className="payment-request-card"
      width="100%"
      minWidth={250}
      maxWidth={350}
      borderRadius={3}
      overflow="hidden"
      p={2}
      sx={{ backgroundColor: variant.cardBackground }}
    >
      <Stack gap={1} p={2} pt={1}>
        <PaymentRequestCardDetails
          publicID={publicID}
          amount={formattedAmount}
          displayName={displayName}
        />
      </Stack>
      <PaymentRequestStatusBar
        variant={variant}
        publicID={publicID}
        canBePaid={canBePaid}
        isAwaitingPayment={isAwaitingPayment}
        isCurrentUserCreditor={isCurrentUserCreditor}
        isPayRestricted={isPayRestricted}
      />
    </Box>
  );
};
